import { cn } from "@/lib/utils";

/** Preset keys — kept stable so they can be used as URL params later. */
export type DateRangePreset = "today" | "24h" | "7d" | "30d" | "all";

interface Option {
  value: DateRangePreset;
  label: string;
}

const OPTIONS: Option[] = [
  { value: "today", label: "Today" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All" },
];

interface Props {
  value: DateRangePreset;
  onChange: (next: DateRangePreset) => void;
}

/**
 * Segmented-pill group for date presets. Most operators think in
 * relative windows ("last 24h", "today") rather than absolute dates,
 * so this is the primary control. Custom ranges would live in a
 * popover trigger if/when needed.
 */
export function DateRangeFilter({ value, onChange }: Props) {
  return (
    <div
      role="radiogroup"
      aria-label="Date range"
      className="inline-flex h-9 items-center rounded-md border border-border bg-transparent p-0.5 text-sm"
    >
      {OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "h-full rounded px-3 text-xs font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-accent",
              active
                ? "bg-accent/20 text-accent ring-1 ring-accent/40"
                : "text-muted-fg hover:bg-muted/60 hover:text-fg",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Convert a preset to an ISO `since` timestamp (or null for "all"). */
export function presetToSince(preset: DateRangePreset): string | undefined {
  const now = new Date();
  switch (preset) {
    case "today": {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    }
    case "24h":
      return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    case "7d":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    case "30d":
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    case "all":
      return undefined;
  }
}
